import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { Box3, Euler, Group, MathUtils, Object3D, Quaternion, Vector3, type Mesh } from 'three';

import { useEngineRuntimeStore } from '../store/engineRuntimeStore';
import { useMapEditorStore } from '../editor/store/mapEditorStore';
import { acquireKeyboardInput, clearAllKeysDown, isKeyDown, releaseKeyboardInput } from '../input/inputState';
import { PROTOTYPE_SKELETON, type PrototypeClip } from '../characters/animation/clips';
import { createPrototypeInstance, type PrototypeInstance, type PrototypeSkin } from '../characters/animation/instance';
import { loadPrototypeGltf } from '../characters/animation/loader';
import { useImportedSkeletonRig } from '../characters/rig/ImportedSkeletonRenderer';
import { applyPose, blendPoses } from '../characters/rendererRegistry';
import type { CharacterPose } from '../content/schema/character';
import {
  findStairCrossing,
  findStairRunOnFloor,
  nearestFloorLevel,
  stairRunFloor,
  stairRunHeight,
  type FloorYMap,
  type StairRun,
  type StairTrigger,
} from './floorStair';
import { controllerDelta, stepWithCollision } from './playerStep';

/** Tunable controller constants. Defaults match a grounded survival-horror feel. */
export interface PlayerConfig {
  moveSpeed: number;
  backpedalSpeed: number;
  strafeSpeed: number;
  flySpeed: number;
  accel: number;
  decel: number;
  /** Physical mouse-look scale; multiplied by the store's 0-1 sensitivity. */
  baseSensitivity: number;
  minPitch: number;
  maxPitch: number;
  /** Camera eye height above the floor plane. */
  eyeHeight: number;
  /** Horizontal collision radius. */
  playerRadius: number;
  bobFreq: number;
  bobVertAmp: number;
  bobHorizAmp: number;
  bobTiltAmp: number;
  strafeLeanMax: number;
  strafeLeanSpeed: number;
  bodyFollowSpeed: number;
  tpDistance: number;
  tpHeightOffset: number;
  tpMinPitch: number;
  cameraBlendSpeed: number;
}

export const DEFAULT_PLAYER_CONFIG: PlayerConfig = {
  moveSpeed: 4.2,
  backpedalSpeed: 2.8,
  strafeSpeed: 4.0,
  flySpeed: 7.5,
  accel: 18,
  decel: 22,
  baseSensitivity: 0.0036,
  minPitch: -1.48,
  maxPitch: 1.48,
  eyeHeight: 1.38,
  playerRadius: 0.28,
  bobFreq: 7.2,
  bobVertAmp: 0.038,
  bobHorizAmp: 0.016,
  bobTiltAmp: 0.008,
  strafeLeanMax: 0.032,
  strafeLeanSpeed: 10,
  bodyFollowSpeed: 12,
  tpDistance: 4.6,
  tpHeightOffset: 1.1,
  tpMinPitch: -0.28,
  cameraBlendSpeed: 7,
};

export interface PlayerMapData {
  floorY: FloorYMap;
  candidateFloors: number[];
  stairTriggers: readonly StairTrigger[];
  /** Flights the player climbs rather than teleports along. */
  stairRuns?: readonly StairRun[];
  isWalkable: (
    x: number,
    z: number,
    radius: number,
    floor: number,
    /** Player's current floor-plane Y; enables the step-height gate when given. */
    playerY?: number,
  ) => boolean;
  spawn: { position: [number, number, number]; yaw: number; floor: number };
}

/** Live per-frame state the controller publishes for game-side body animation. */
export interface PlayerMotionState {
  yaw: number;
  pitch: number;
  blend: number;
  speed: number;
  isMoving: boolean;
  velocityX: number;
  velocityZ: number;
  floor: number;
  x: number;
  y: number;
  z: number;
}

export interface PlayerInteractContext {
  x: number;
  z: number;
  floor: number;
}

export interface PlayerCallbacks {
  /** Per-frame hook for game logic (resource drain, broadcast, prompts, etc.). */
  onTick?: (dt: number, motion: PlayerMotionState) => void;
  /** Interact key pressed near the player's current position. */
  onInteract?: (ctx: PlayerInteractContext) => void;
  /** The game-defined primary-action key was pressed. */
  onPrimaryAction?: () => void;
  /** A footstep should play at the given 0-1 intensity. */
  onFootstep?: (intensity: number) => void;
  /** The player crossed a stair trigger to another floor. */
  onStairTransition?: () => void;
  /** Pointer was acquired/released. */
  onLockChange: (locked: boolean) => void;
  /** The browser refused a pointer-lock request (permissions, iframe
   * restrictions, etc.) - the "Click to capture mouse" hint has no other way
   * to know this happened, since `requestPointerLock()`'s own promise
   * rejection is browser-inconsistent and some browsers only ever report
   * this via the `pointerlockerror` event instead. */
  onLockError?: () => void;
}

/** A general external camera-override target: pan/snap the camera to
 * `approach` and look at `lookAt`, taking over from player input until the
 * ref is cleared back to null. Mirrors the map editor's own `FocusTarget`
 * shape (`editor/store/mapEditorStore.ts`), minus its editor-only bookkeeping
 * (`instanceId`, floor-snapped Y) - a runtime cutscene player (SF23.2) writes
 * one of these each frame while its camera track is in `'take'`/`'hold'`,
 * and sets the ref back to null on `'release'`. */
export interface CameraOverrideTarget {
  /** World-space camera position to pan or snap to. */
  approach: [number, number, number];
  /** World-space point the camera should look at. */
  lookAt: [number, number, number];
  /** Skip the pan and snap immediately - a hard cut. */
  instant?: boolean;
}

/** A one-shot teleport request: move the player to `x`/`z` on `floor`
 * immediately. Consumed and cleared back to `null` the next frame it is
 * read, mirroring the stair-trigger transition this is modelled on (see the
 * "Stair transition" block in the movement loop) - a caller sets it once per
 * request, never toggles it back off itself. */
export interface TeleportRequest {
  floor: number;
  x: number;
  z: number;
}

export interface FirstPersonControllerProps {
  config?: Partial<PlayerConfig>;
  mapData: PlayerMapData;
  callbacks: PlayerCallbacks;
  /** External camera-shake trigger (e.g. impacts). Read and reset each frame. */
  externalShakeRef?: MutableRefObject<number>;
  /** General external camera override (e.g. a running cutscene). Takes
   * priority over the editor's own focus-pan when set; read every frame,
   * not reset by this component - the driver owns clearing it. */
  externalCameraOverrideRef?: MutableRefObject<CameraOverrideTarget | null>;
  /** A pending teleport (e.g. from a `MapTriggerVolume` of kind `'teleport'`
   * dispatched through the trigger registry). Applied and cleared on the
   * next frame it is non-null; gated the same way as the stair-trigger
   * transition below, so a running cutscene cannot be fought by a teleport
   * landing mid-pan. */
  externalTeleportRef?: MutableRefObject<TeleportRequest | null>;
  /** Receives live motion state each frame for body animation. */
  motionStateRef?: MutableRefObject<PlayerMotionState | null>;
  /** Rendered inside the yaw-following body group (e.g. a character mesh). */
  children?: ReactNode;
}

/**
 * Reusable first/third-person controller: WASD + mouse-look, acceleration,
 * head-bob, strafe-lean, FP/TP camera blend, floor-aware collision, stair
 * transitions, fly mode, camera shake, and editor focus-panning. Game-specific
 * effects (audio, resource drain, interaction outcomes) are delegated via
 * callbacks so the controller never depends on any game.
 */
export function FirstPersonController({
  config: configOverride,
  mapData,
  callbacks,
  externalShakeRef,
  externalCameraOverrideRef,
  externalTeleportRef,
  motionStateRef,
  children,
}: FirstPersonControllerProps) {
  const { camera } = useThree();
  const config = useMemo(() => ({ ...DEFAULT_PLAYER_CONFIG, ...configOverride }), [configOverride]);
  const configRef = useRef(config);
  configRef.current = config;

  const { onLockChange } = callbacks;
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const mapDataRef = useRef(mapData);
  mapDataRef.current = mapData;

  // ── Scene refs ───────────────────────────────────────────────────────────
  const playerRef = useRef<Group | null>(null);
  const upperBodyRef = useRef<Group | null>(null);

  // ── Reusable vectors ───────────────────────────────────────────────────────
  const fwd = useMemo(() => new Vector3(), []);
  const rgt = useMemo(() => new Vector3(), []);
  const movDir = useMemo(() => new Vector3(), []);
  const targetVel = useMemo(() => new Vector3(), []);
  const eyePos = useMemo(() => new Vector3(), []);
  const tpPos = useMemo(() => new Vector3(), []);
  const tpLook = useMemo(() => new Vector3(), []);
  const blendPos = useMemo(() => new Vector3(), []);
  const fpEuler = useMemo(() => { const e = new Euler(); e.order = 'YXZ'; return e; }, []);
  // Scratch for the first/third-person blend below - the one place in this
  // file that still allocated fresh objects every frame instead of reusing
  // pre-built ones the way every other per-frame vector/euler here does.
  const prevRot = useMemo(() => new Quaternion(), []);
  const tpQuat = useMemo(() => new Quaternion(), []);

  // ── Input + physics state ──────────────────────────────────────────────────
  // Movement/fly keys are read live from the shared `inputState` module
  // (see `../input/inputState.ts`) inside the per-frame update below, not
  // tracked in a local ref - `acquireKeyboardInput`/`releaseKeyboardInput`
  // below join/leave that module's single shared keyboard listener.
  const velocity = useRef(new Vector3());
  const yaw = useRef(mapData.spawn.yaw);
  const pitch = useRef(-0.08);
  const bobTime = useRef(0);
  const stepTimer = useRef(0);
  const strafeSmooth = useRef(0);
  const leanSmooth = useRef(0);
  const lockedRef = useRef(false);
  const dragRef = useRef(false);
  const lastPtrRef = useRef<{ x: number; y: number } | null>(null);
  const blend = useRef(0);
  const blendTarget = useRef(0);
  const shakeIntensity = useRef(0);
  const shakeDecay = useRef(0);
  const currentFloorRef = useRef(mapData.spawn.floor);
  const stairCooldown = useRef(0);
  const lastModeRef = useRef<'first' | 'third'>('first');
  const previousFlyModeRef = useRef(false);

  // ── Engine store (settings + tool flags) ───────────────────────────────────
  const setCameraMode = useEngineRuntimeStore((s) => s.setCameraMode);
  const setCurrentFloor = useEngineRuntimeStore((s) => s.setCurrentFloor);
  const cameraModePref = useEngineRuntimeStore((s) => s.cameraModePref);
  const cameraModeForced = useEngineRuntimeStore((s) => s.cameraModeForced);
  const mouseSensitivity = useEngineRuntimeStore((s) => s.mouseSensitivity);
  const invertY = useEngineRuntimeStore((s) => s.invertY);
  const keyBindings = useEngineRuntimeStore((s) => s.keyBindings);
  const headBob = useEngineRuntimeStore((s) => s.headBob);
  const moveSpeedMultiplier = useEngineRuntimeStore((s) => s.moveSpeedMultiplier);
  const devConsoleOpen = useEngineRuntimeStore((s) => s.devConsoleOpen);
  const devFlyMode = useEngineRuntimeStore((s) => s.devFlyMode);
  const paused = useEngineRuntimeStore((s) => s.paused);
  const devTeleportRequest = useEngineRuntimeStore((s) => s.devTeleportRequest);
  const clearDevTeleportRequest = useEngineRuntimeStore((s) => s.clearDevTeleportRequest);

  // Editor (engine tool)
  const editorEnabled = useMapEditorStore((s) => s.enabled);
  const editorInteractionMode = useMapEditorStore((s) => s.interactionMode);
  const editorFocusTarget = useMapEditorStore((s) => s.focusTarget);
  const editorClearFocusTarget = useMapEditorStore((s) => s.clearFocusTarget);
  const playtesting = useMapEditorStore((s) => s.playtesting);
  const playtestSpawnPosition = useMapEditorStore((s) => s.playtestSpawnPosition);
  const playtestSpawnFloor = useMapEditorStore((s) => s.playtestSpawnFloor);
  const savedPlayerState = useMapEditorStore((s) => s.savedPlayerState);

  // Latest-value refs for event-handler closures
  const sensitivityRef = useRef(mouseSensitivity); sensitivityRef.current = mouseSensitivity;
  const invertYRef = useRef(invertY); invertYRef.current = invertY;
  const bindingsRef = useRef(keyBindings); bindingsRef.current = keyBindings;
  const headBobRef = useRef(headBob); headBobRef.current = headBob;
  const moveSpeedRef = useRef(moveSpeedMultiplier); moveSpeedRef.current = moveSpeedMultiplier;
  const devConsoleOpenRef = useRef(devConsoleOpen); devConsoleOpenRef.current = devConsoleOpen;
  const pausedRef = useRef(paused); pausedRef.current = paused;
  const devFlyModeRef = useRef(devFlyMode); devFlyModeRef.current = devFlyMode;
  const editorEnabledRef = useRef(editorEnabled); editorEnabledRef.current = editorEnabled;
  const editorInteractionModeRef = useRef(editorInteractionMode); editorInteractionModeRef.current = editorInteractionMode;
  const cameraModeForcedRef = useRef(cameraModeForced); cameraModeForcedRef.current = cameraModeForced;

  // ── Spawn ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playerRef.current) return;
    const { spawn, floorY } = mapDataRef.current;
    const baseY = floorY[spawn.floor] ?? 0;
    playerRef.current.position.set(spawn.position[0], baseY, spawn.position[2]);
    playerRef.current.rotation.y = spawn.yaw;
    yaw.current = spawn.yaw;
    currentFloorRef.current = spawn.floor;
    camera.rotation.order = 'YXZ';
    camera.position.set(spawn.position[0], baseY + configRef.current.eyeHeight, spawn.position[2]);
    camera.rotation.set(pitch.current, yaw.current, 0);
  }, [camera]);

  // Apply the camera mode: a creator's forced project setting wins over the
  // player's own preference, and stays reactive (a project-level lock, not a
  // background setting) so it takes effect even if it resolves after mount.
  // The unforced case still only applies cameraModePref once on mount,
  // matching the existing player-settings behaviour elsewhere in this store
  // (a background pref change takes effect on the next toggle, not instantly).
  useEffect(() => {
    if (cameraModeForced) { blendTarget.current = cameraModeForced === 'third' ? 1 : 0; return; }
    blendTarget.current = cameraModePref === 'third' ? 1 : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraModeForced]);

  // Release input when the dev console opens. Movement/fly keys need no
  // explicit reset here any more - the per-frame update below reads them
  // live from the shared `inputState` module, gated on `devConsoleOpen`
  // (via `inputSuppressed`) at the read site itself.
  useEffect(() => {
    if (!devConsoleOpen) return;
    dragRef.current = false;
    lastPtrRef.current = null;
    velocity.current.set(0, 0, 0);
    if (document.pointerLockElement instanceof HTMLElement) {
      void document.exitPointerLock();
    }
    onLockChange(false);
  }, [devConsoleOpen, onLockChange]);

  // Release input while paused, and drop pointer lock so the pause UI is
  // usable. Mirrors the dev-console effect above (see its comment - the same
  // applies to `pausedRef` in `inputSuppressed` below).
  useEffect(() => {
    if (!paused) return;
    dragRef.current = false;
    lastPtrRef.current = null;
    velocity.current.set(0, 0, 0);
    if (document.pointerLockElement instanceof HTMLElement) {
      void document.exitPointerLock();
    }
    onLockChange(false);
  }, [paused, onLockChange]);

  // Release input in edit mode (unless fly mode is active)
  useEffect(() => {
    if (!editorEnabled || editorInteractionMode !== 'edit' || devFlyMode) return;
    dragRef.current = false;
    lastPtrRef.current = null;
    velocity.current.set(0, 0, 0);
    if (document.pointerLockElement instanceof HTMLElement) {
      void document.exitPointerLock();
    }
    onLockChange(false);
  }, [editorEnabled, editorInteractionMode, devFlyMode, onLockChange]);

  // Settle onto the nearest floor when leaving fly mode
  useEffect(() => {
    const player = playerRef.current;
    if (!player) { previousFlyModeRef.current = devFlyMode; return; }
    if (previousFlyModeRef.current && !devFlyMode) {
      const { floorY, candidateFloors } = mapDataRef.current;
      const nearestFloor = nearestFloorLevel(player.position.y, floorY, candidateFloors, currentFloorRef.current);
      player.position.y = floorY[nearestFloor] ?? player.position.y;
      currentFloorRef.current = nearestFloor;
      setCurrentFloor(nearestFloor);
      velocity.current.y = 0;
    }
    previousFlyModeRef.current = devFlyMode;
  }, [devFlyMode, setCurrentFloor]);

  // Playtest in place reactive teleportation
  useEffect(() => {
    if (!playerRef.current) return;
    if (playtesting && playtestSpawnPosition) {
      const targetY = mapDataRef.current.floorY[playtestSpawnFloor ?? 0] ?? 0;
      playerRef.current.position.set(playtestSpawnPosition[0], targetY, playtestSpawnPosition[2]);
      currentFloorRef.current = playtestSpawnFloor ?? 0;
      setCurrentFloor(playtestSpawnFloor ?? 0);
      velocity.current.set(0, 0, 0);
      yaw.current = 0;
      pitch.current = 0;
      
      const shell = document.getElementById('app-shell');
      if (shell && shell.requestPointerLock) {
        setTimeout(() => {
          shell.requestPointerLock();
        }, 100);
      }
    } else if (!playtesting && savedPlayerState) {
      const targetY = mapDataRef.current.floorY[savedPlayerState.floor] ?? 0;
      playerRef.current.position.set(savedPlayerState.position[0], targetY, savedPlayerState.position[2]);
      currentFloorRef.current = savedPlayerState.floor;
      setCurrentFloor(savedPlayerState.floor);
      velocity.current.set(0, 0, 0);
      yaw.current = savedPlayerState.yaw;
      pitch.current = savedPlayerState.pitch;
    }
  }, [playtesting, playtestSpawnPosition, playtestSpawnFloor, savedPlayerState, setCurrentFloor]);

  // Dev-console teleport: a one-shot position override (e.g. a game's `tp`
  // command), consumed and cleared immediately so it never re-fires on an
  // unrelated re-render. Unlike the playtest respawn above, this leaves the
  // camera's current look direction alone - it is a QA "walk me over there",
  // not a respawn.
  useEffect(() => {
    if (!playerRef.current || !devTeleportRequest) return;
    const targetY = mapDataRef.current.floorY[devTeleportRequest.floor] ?? 0;
    playerRef.current.position.set(devTeleportRequest.x, targetY, devTeleportRequest.z);
    currentFloorRef.current = devTeleportRequest.floor;
    setCurrentFloor(devTeleportRequest.floor);
    velocity.current.set(0, 0, 0);
    clearDevTeleportRequest();
  }, [devTeleportRequest, setCurrentFloor, clearDevTeleportRequest]);

  // ── Input handlers ─────────────────────────────────────────────────────────
  useEffect(() => {
    const applyLook = (dx: number, dy: number) => {
      if (devConsoleOpenRef.current) return;
      if (editorEnabledRef.current && editorInteractionModeRef.current === 'edit' && !devFlyModeRef.current) return;
      const cfg = configRef.current;
      const s = cfg.baseSensitivity * sensitivityRef.current * 2;
      yaw.current -= dx * s;
      const pitchDelta = (invertYRef.current ? dy : -dy) * s;
      const pMin = blendTarget.current > 0.5 ? cfg.tpMinPitch : cfg.minPitch;
      pitch.current = MathUtils.clamp(pitch.current + pitchDelta, pMin, cfg.maxPitch);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        if (useMapEditorStore.getState().playtesting) {
          useMapEditorStore.getState().exitPlaytest();
          return;
        }
      }
      if (devConsoleOpenRef.current) return;
      // A resolved match outcome (GameRuntime's own Logic evaluator) sets
      // this the same way Escape mid-play would - stop accepting further
      // gameplay input entirely. Movement/fly keys are already covered by
      // `inputSuppressed` in the per-frame update below; this is the one
      // dispatch path (key-down, so interact, primary action, camera
      // toggle) that needs its own gate, mirroring the dev-console early
      // return right above.
      if (pausedRef.current) return;
      if (editorEnabledRef.current && editorInteractionModeRef.current === 'edit' && !devFlyModeRef.current) return;
      // A running cutscene has taken the camera - see the doc comment on
      // externalCameraOverrideRef. It already wins every frame in the
      // movement loop below (via `inputSuppressed`); this is the one
      // dispatch path (key-down, so interact, primary action, camera
      // toggle) that was not yet gated on it.
      if (externalCameraOverrideRef?.current) return;
      if (e.repeat) return;
      // Movement/fly keys (moveForward/Back/Left/Right, Space, Shift) are no
      // longer dispatched from here - the per-frame update below polls them
      // live from the shared `inputState` module instead (see that module's
      // own doc comment for why "is this held right now" is read, not
      // event-dispatched). Only the one-shot actions below still need a
      // keydown-edge dispatch.
      const b = bindingsRef.current;
      if (e.code === b.toggleCamera) {
        if (cameraModeForcedRef.current) return;
        blendTarget.current = blendTarget.current > 0.5 ? 0 : 1;
        return;
      }
      if (e.code === b.primaryAction) { callbacksRef.current.onPrimaryAction?.(); return; }
      if (e.code === b.interact) {
        const player = playerRef.current;
        if (!player) return;
        callbacksRef.current.onInteract?.({
          x: player.position.x,
          z: player.position.z,
          floor: currentFloorRef.current,
        });
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (devConsoleOpenRef.current) return;
      if (pausedRef.current) return;
      if (editorEnabledRef.current && editorInteractionModeRef.current === 'edit' && !devFlyModeRef.current) return;
      if (lockedRef.current) { applyLook(e.movementX, e.movementY); return; }
      if (!dragRef.current) return;
      const last = lastPtrRef.current;
      if (!last) { lastPtrRef.current = { x: e.clientX, y: e.clientY }; return; }
      applyLook(e.clientX - last.x, e.clientY - last.y);
      lastPtrRef.current = { x: e.clientX, y: e.clientY };
    };

    // Touch has no `mousemove`/pointer-lock movementX equivalent, so a
    // touch-and-drag look needs its own listener: same drag-fallback math as
    // onMouseMove's non-locked branch, but driven by pointermove (which touch
    // does fire) and scoped to non-mouse pointers so a real mouse drag isn't
    // double-applied by both handlers at once.
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      if (devConsoleOpenRef.current) return;
      if (pausedRef.current) return;
      if (editorEnabledRef.current && editorInteractionModeRef.current === 'edit' && !devFlyModeRef.current) return;
      if (!dragRef.current) return;
      const last = lastPtrRef.current;
      if (!last) { lastPtrRef.current = { x: e.clientX, y: e.clientY }; return; }
      applyLook(e.clientX - last.x, e.clientY - last.y);
      lastPtrRef.current = { x: e.clientX, y: e.clientY };
    };

    const onPointerDown = (e: PointerEvent) => {
      if (devConsoleOpenRef.current) return;
      if (pausedRef.current) return;
      if (editorEnabledRef.current && editorInteractionModeRef.current === 'edit' && !devFlyModeRef.current) return;
      if (e.button !== 0) return;
      const shell = document.getElementById('app-shell');
      if (!(e.target instanceof Node) || !shell?.contains(e.target)) return;
      dragRef.current = true;
      lastPtrRef.current = { x: e.clientX, y: e.clientY };
      onLockChange(true);
      if (shell && document.pointerLockElement !== shell && shell.requestPointerLock) {
        const r = shell.requestPointerLock();
        // Browser-inconsistent: some reject this promise on refusal, others
        // only ever fire the 'pointerlockerror' document event below - report
        // through both so the caller does not have to guess which applies.
        if (r && typeof r.catch === 'function') {
          void r.catch(() => {
            dragRef.current = false;
            callbacksRef.current.onLockError?.();
          });
        }
      }
    };

    const onPointerUp = () => {
      if (lockedRef.current) return;
      dragRef.current = false;
      lastPtrRef.current = null;
      onLockChange(false);
    };

    const onBlur = () => {
      // A physically-held key's `keyup` is not guaranteed to arrive after
      // e.g. an alt-tab on every platform - force every held key up in the
      // shared module rather than trusting one to follow.
      clearAllKeysDown();
      dragRef.current = false;
      lastPtrRef.current = null;
      if (!lockedRef.current) onLockChange(false);
    };

    const onPtrLockChange = () => {
      const locked = document.pointerLockElement === document.getElementById('app-shell');
      lockedRef.current = locked;
      if (locked) {
        dragRef.current = false;
        lastPtrRef.current = null;
        onLockChange(true);
        return;
      }
      if (!dragRef.current) onLockChange(false);
    };

    // Some browsers never reject requestPointerLock()'s own promise on
    // refusal (permissions, an unfocused document, an iframe without the
    // right allow policy) and only ever report it here - without this, a
    // refused lock left the "Click to capture mouse" hint sitting with no
    // failure state or retry guidance at all.
    const onPtrLockError = () => {
      dragRef.current = false;
      callbacksRef.current.onLockError?.();
    };

    // Resync from the browser's actual pointer-lock state on mount, rather
    // than assuming unlocked. A game can remount this whole component while
    // `#app-shell` itself stays mounted and still locked underneath it (e.g.
    // a place transition that keys a fresh scene per arrival) - nothing
    // released the lock, so `document.pointerLockElement` still names the
    // shell, but a fresh `lockedRef`/`dragRef` (both initialised to
    // false above) and no `pointerlockchange` event ever fires to tell this
    // new instance, since the browser's own lock state never actually
    // changed. Left alone, that stranded the player with a dead mouse and a
    // stale "click to begin" hint until something else forced a real
    // lock-state change - in practice only Escape, which the app also reads
    // as "toggle pause", so recovering took an Escape-Escape-click dance.
    // Reading the real state up front makes the remount transparent instead.
    const shell = document.getElementById('app-shell');
    const alreadyLocked = document.pointerLockElement === shell;
    lockedRef.current = alreadyLocked;
    onLockChange(alreadyLocked);

    // Joins the shared keyboard listener (see ../input/inputState.ts) that
    // the per-frame update below polls movement/fly keys from - reference
    // counted, so this is safe alongside TouchControlsOverlay's own
    // interest in the same shared listener.
    acquireKeyboardInput();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('pointerlockchange', onPtrLockChange);
    document.addEventListener('pointerlockerror', onPtrLockError);

    return () => {
      releaseKeyboardInput();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('pointerlockchange', onPtrLockChange);
      document.removeEventListener('pointerlockerror', onPtrLockError);
    };
    // externalCameraOverrideRef is a MutableRefObject the caller owns for the
    // component's lifetime (same stability guarantee as the internal xRef
    // pattern this effect already relies on for everything else it reads);
    // re-attaching every window listener on every write to its .current
    // would defeat the point of reading it live instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLockChange]);

  // ── Per-frame update ─────────────────────────────────────────────────────
  useFrame((_, rawDt) => {
    const player = playerRef.current;
    const upperBody = upperBodyRef.current;
    if (!player || !upperBody) return;
    // Capped so a long frame (a hitch, a hidden tab) plays as slow motion
    // rather than moving the player metres in one step (playerStep.ts).
    const dt = controllerDelta(rawDt);
    const cfg = configRef.current;
    const map = mapDataRef.current;

    if (stairCooldown.current > 0) stairCooldown.current -= dt;

    // ── External camera override (e.g. a running cutscene) ─────────────────
    // Same pan-and-look mechanic as the editor focus pan just below,
    // generalised to a plain ref instead of the editor store, and without
    // that block's floor-snap/auto-clear bookkeeping - the driver (the
    // cutscene player) owns writing and clearing this every frame instead.
    if (externalCameraOverrideRef?.current) {
      const override = externalCameraOverrideRef.current;
      if (override.instant) {
        player.position.set(override.approach[0], override.approach[1], override.approach[2]);
      } else {
        const lerpRate = 1 - Math.exp(-dt * 5.8);
        player.position.x += (override.approach[0] - player.position.x) * lerpRate;
        player.position.y += (override.approach[1] - player.position.y) * lerpRate;
        player.position.z += (override.approach[2] - player.position.z) * lerpRate;
      }

      const dxLook = override.lookAt[0] - player.position.x;
      const dzLook = override.lookAt[2] - player.position.z;
      const dyLook = override.lookAt[1] - (player.position.y + cfg.eyeHeight);
      const desiredYaw = Math.atan2(-dxLook, -dzLook);
      const horiz = Math.hypot(dxLook, dzLook);
      const desiredPitch = MathUtils.clamp(Math.atan2(dyLook, horiz), cfg.minPitch, cfg.maxPitch);
      const yawDelta = MathUtils.euclideanModulo(desiredYaw - yaw.current + Math.PI, Math.PI * 2) - Math.PI;
      const yawLerp = 1 - Math.exp(-dt * 7.5);
      yaw.current += yawDelta * yawLerp;
      pitch.current += (desiredPitch - pitch.current) * yawLerp;
      velocity.current.set(0, 0, 0);
    } else if (editorFocusTarget && editorEnabled && editorInteractionMode === 'edit') {
      const target = editorFocusTarget;
      const targetY = map.floorY[target.floor] ?? 0;
      if (target.instant) {
        player.position.set(target.approach[0], targetY, target.approach[2]);
        currentFloorRef.current = target.floor;
        setCurrentFloor(target.floor);
        useMapEditorStore.setState({ focusTarget: { ...target, instant: false } });
      } else {
        const lerpRate = 1 - Math.exp(-dt * 5.8);
        player.position.x += (target.approach[0] - player.position.x) * lerpRate;
        player.position.z += (target.approach[2] - player.position.z) * lerpRate;
        player.position.y = targetY;
      }

      const dxLook = target.lookAt[0] - player.position.x;
      const dzLook = target.lookAt[2] - player.position.z;
      const dyLook = target.lookAt[1] - (player.position.y + cfg.eyeHeight);
      const desiredYaw = Math.atan2(-dxLook, -dzLook);
      const horiz = Math.hypot(dxLook, dzLook);
      const desiredPitch = MathUtils.clamp(Math.atan2(dyLook, horiz), cfg.minPitch, cfg.maxPitch);
      const yawDelta = MathUtils.euclideanModulo(desiredYaw - yaw.current + Math.PI, Math.PI * 2) - Math.PI;
      const yawLerp = 1 - Math.exp(-dt * 7.5);
      yaw.current += yawDelta * yawLerp;
      pitch.current += (desiredPitch - pitch.current) * yawLerp;
      velocity.current.set(0, 0, 0);

      const planarDist = Math.hypot(target.approach[0] - player.position.x, target.approach[2] - player.position.z);
      if (planarDist < 0.05 && Math.abs(yawDelta) < 0.02) editorClearFocusTarget();
    }

    // ── Camera-relative basis ─────────────────────────────────────────────
    fwd.set(-Math.sin(yaw.current), 0, -Math.cos(yaw.current));
    rgt.set(Math.cos(yaw.current), 0, -Math.sin(yaw.current));

    // ── Movement ──────────────────────────────────────────────────────────
    // Movement/fly input is suppressed (read as if no key is held) under the
    // exact same conditions that used to stop the old local `keys` ref from
    // ever being set in the first place: the dev console open, the game
    // paused, editor edit mode owning input (unless dev fly mode is active),
    // or a cutscene/editor focus pan having taken the camera (both handled
    // just above). `inputState.isKeyDown` is raw, ungated physical/virtual
    // key state shared with every other reader, so the gating that used to
    // live at the write site (onKeyDown skipping, or a ref reset) now lives
    // here at the read site instead.
    const inputSuppressed =
      devConsoleOpenRef.current ||
      pausedRef.current ||
      (editorEnabledRef.current && editorInteractionModeRef.current === 'edit' && !devFlyModeRef.current) ||
      Boolean(externalCameraOverrideRef?.current);
    const bindings = bindingsRef.current;

    movDir.set(0, 0, 0);
    if (!inputSuppressed) {
      if (isKeyDown(bindings.moveForward)) movDir.addScaledVector(fwd, 1);
      if (isKeyDown(bindings.moveBack)) movDir.addScaledVector(fwd, -1);
      if (isKeyDown(bindings.moveLeft)) movDir.addScaledVector(rgt, -1);
      if (isKeyDown(bindings.moveRight)) movDir.addScaledVector(rgt, 1);
    }

    const hasInput = movDir.lengthSq() > 0;
    const flyVertical = !inputSuppressed
      ? (isKeyDown('Space') ? 1 : 0) - ((isKeyDown('ShiftLeft') || isKeyDown('ShiftRight')) ? 1 : 0)
      : 0;
    const isFlying = devFlyModeRef.current;
    const hasMovementInput = hasInput || (isFlying && flyVertical !== 0);
    const speedMul = moveSpeedRef.current;
    if (hasMovementInput) {
      movDir.normalize();
      if (isFlying) {
        targetVel
          .copy(fwd)
          .multiplyScalar(movDir.dot(fwd) * cfg.flySpeed * speedMul)
          .addScaledVector(rgt, movDir.dot(rgt) * cfg.flySpeed * speedMul);
        targetVel.y = flyVertical * cfg.flySpeed * speedMul;
      } else {
        const fwdDot = movDir.dot(fwd);
        const rgtDot = movDir.dot(rgt);
        const fwdSpeed = fwdDot > 0 ? cfg.moveSpeed * speedMul : cfg.backpedalSpeed * speedMul;
        targetVel
          .copy(fwd).multiplyScalar(fwdDot * fwdSpeed)
          .addScaledVector(rgt, rgtDot * cfg.strafeSpeed * speedMul);
        targetVel.y = 0;
      }
    } else {
      targetVel.set(0, 0, 0);
    }

    const velLerp = 1 - Math.exp(-dt * (hasMovementInput ? cfg.accel : cfg.decel));
    velocity.current.lerp(targetVel, velLerp);

    const speed = Math.hypot(velocity.current.x, velocity.current.z);
    const isMoving = speed > 0.08;
    const floor = currentFloorRef.current;

    // ── Collision-aware position update ───────────────────────────────────
    if (isFlying) {
      player.position.addScaledVector(velocity.current, dt);
    } else if (isMoving) {
      // Substepped, so no single walkability test is further than
      // MAX_STEP_DISTANCE from the last one and a thin wall cannot be skipped.
      stepWithCollision(player.position, velocity.current, dt, cfg.playerRadius, floor, player.position.y, map.isWalkable);
    }

    // ── External teleport request ──────────────────────────────────────────
    // A `MapTriggerVolume` of kind 'teleport' dispatched this frame. Gated
    // on externalCameraOverrideRef the same way the stair-trigger transition
    // below is, for the same reason: a running cutscene owns player.position
    // and this would otherwise fight it. Consumed once, then cleared.
    if (externalTeleportRef?.current && !externalCameraOverrideRef?.current) {
      const request = externalTeleportRef.current;
      externalTeleportRef.current = null;
      currentFloorRef.current = request.floor;
      setCurrentFloor(request.floor);
      const targetY = map.floorY[request.floor] ?? player.position.y;
      player.position.set(request.x, targetY, request.z);
      velocity.current.set(0, 0, 0);
      camera.position.set(request.x, targetY + cfg.eyeHeight, request.z);
    }

    // ── Stair runs: climb, do not teleport ────────────────────────────────
    // A run is a sloped walkable surface, so the player's height comes from
    // where they are along it and their floor flips at the halfway point. That
    // is what lets them stop on the sixth step and look back down. Gated on
    // externalCameraOverrideRef same as the movement/collision block above -
    // a running cutscene owns player.position outright and this would
    // otherwise fight it, snapping height/floor from wherever the pan
    // currently sits.
    //
    // `findStairRunOnFloor` (not the raw `findStairRun`) matches only a run
    // that actually connects to the player's current floor - the same guard
    // the collision predicate above already applies via `map.isWalkable`
    // (see `makeWalkable`'s own use of it). Without it, a run whose footprint
    // happens to coincide with an ordinary room two floors away (two storeys
    // sharing one footprint is normal - a cellar flight under an unrelated
    // upstairs room, say) would hijack that room's height and floor the
    // moment the player walked into it: this block used to snap the player
    // down to the far run's height/floor from pure XZ coincidence, with no
    // regard for which floor they were actually stood on.
    if (!isFlying && !externalCameraOverrideRef?.current) {
      const runs = map.stairRuns ?? [];
      const run = runs.length > 0
        ? findStairRunOnFloor(player.position.x, player.position.z, currentFloorRef.current, runs)
        : null;
      if (run) {
        player.position.y = stairRunHeight(player.position.x, player.position.z, run, map.floorY);
        const runFloor = stairRunFloor(player.position.x, player.position.z, run);
        if (runFloor !== currentFloorRef.current) {
          currentFloorRef.current = runFloor;
          setCurrentFloor(runFloor);
        }
      } else if (runs.length > 0) {
        // Off a flight: settle onto the plane of whichever floor we are on.
        const planeY = map.floorY[currentFloorRef.current];
        if (planeY !== undefined) player.position.y = planeY;
      }
    }

    // ── Stair transition ──────────────────────────────────────────────────
    // Same externalCameraOverrideRef gate as the stair-run block above: a
    // cutscene panning through a stair trigger volume must not teleport the
    // player's real floor/position or fire onStairTransition mid-cutscene.
    if (!isFlying && !externalCameraOverrideRef?.current && stairCooldown.current <= 0) {
      const trigger = findStairCrossing(player.position.x, player.position.z, floor, map.stairTriggers);
      if (trigger) {
        currentFloorRef.current = trigger.toFloor;
        setCurrentFloor(trigger.toFloor);
        player.position.x = trigger.spawnXZ[0];
        player.position.z = trigger.spawnXZ[1];
        player.position.y = trigger.toY;
        velocity.current.set(0, 0, 0);
        stairCooldown.current = 1.2;
        callbacksRef.current.onStairTransition?.();
        camera.position.y = (map.floorY[trigger.toFloor] ?? 0) + cfg.eyeHeight;
      }
    }

    // ── Footsteps + head-bob cadence ──────────────────────────────────────
    if (isMoving && !isFlying) {
      bobTime.current += dt * (cfg.bobFreq + speed * 0.4);
      stepTimer.current -= dt;
      if (stepTimer.current <= 0) {
        const t = Math.min(speed / cfg.moveSpeed, 1);
        stepTimer.current = MathUtils.lerp(0.52, 0.30, t);
        callbacksRef.current.onFootstep?.(MathUtils.lerp(0.6, 1.0, t));
      }
    } else {
      bobTime.current = MathUtils.lerp(bobTime.current, Math.round(bobTime.current / Math.PI) * Math.PI, 1 - Math.exp(-dt * 6));
      stepTimer.current = 0.18;
    }

    // ── Body container follows yaw + leans with pitch ─────────────────────
    player.rotation.y = MathUtils.damp(player.rotation.y, yaw.current, cfg.bodyFollowSpeed, dt);
    upperBody.rotation.x = MathUtils.damp(upperBody.rotation.x, pitch.current * 0.28, 8, dt);

    // ── Camera blend ──────────────────────────────────────────────────────
    blend.current = MathUtils.lerp(blend.current, blendTarget.current, 1 - Math.exp(-dt * cfg.cameraBlendSpeed));
    const b = blend.current;
    const resolvedMode: 'first' | 'third' = b > 0.5 ? 'third' : 'first';
    if (resolvedMode !== lastModeRef.current) {
      lastModeRef.current = resolvedMode;
      setCameraMode(resolvedMode);
    }

    // ── Strafe lean ───────────────────────────────────────────────────────
    const rawStrafe = inputSuppressed ? 0 : (isKeyDown(bindings.moveRight) ? 1 : 0) - (isKeyDown(bindings.moveLeft) ? 1 : 0);
    strafeSmooth.current = MathUtils.lerp(strafeSmooth.current, rawStrafe, 1 - Math.exp(-dt * cfg.strafeLeanSpeed));
    const targetLean = -strafeSmooth.current * cfg.strafeLeanMax * (1 - b);
    leanSmooth.current = MathUtils.lerp(leanSmooth.current, targetLean, 1 - Math.exp(-dt * cfg.strafeLeanSpeed));

    // ── Head-bob ──────────────────────────────────────────────────────────
    const bobFade = (1 - b) * (isMoving && headBobRef.current ? 1 : 0);
    const speedT = Math.min(speed / cfg.moveSpeed, 1);
    const bobY = Math.sin(bobTime.current) * cfg.bobVertAmp * bobFade * speedT;
    const bobX = Math.sin(bobTime.current * 0.5) * cfg.bobHorizAmp * bobFade * speedT;
    const bobRoll = Math.sin(bobTime.current * 0.5) * cfg.bobTiltAmp * bobFade * speedT;

    const floorY = player.position.y;
    // Both offsets below are along `rgt`/`-fwd` (see the camera-relative basis
    // above: rgt = (cos(yaw), 0, -sin(yaw)), -fwd = (sin(yaw), 0, cos(yaw))).
    eyePos.set(
      player.position.x + Math.cos(yaw.current) * bobX,
      floorY + cfg.eyeHeight + bobY,
      player.position.z - Math.sin(yaw.current) * bobX,
    );

    const tpPitch = Math.max(pitch.current, cfg.tpMinPitch);
    tpPos.set(
      player.position.x + Math.sin(yaw.current) * cfg.tpDistance * Math.cos(tpPitch),
      floorY + cfg.eyeHeight + cfg.tpHeightOffset - Math.sin(tpPitch) * cfg.tpDistance,
      player.position.z + Math.cos(yaw.current) * cfg.tpDistance * Math.cos(tpPitch),
    );
    tpLook.set(player.position.x, floorY + cfg.eyeHeight + 0.3, player.position.z);

    blendPos.copy(eyePos).lerp(tpPos, b);
    camera.position.lerp(blendPos, 1 - Math.exp(-dt * 14));

    if (b < 0.98) {
      fpEuler.set(pitch.current, yaw.current, leanSmooth.current + bobRoll);
      camera.rotation.copy(fpEuler);
    }
    if (b > 0.02) {
      prevRot.copy(camera.quaternion);
      camera.lookAt(tpLook);
      tpQuat.copy(camera.quaternion);
      camera.quaternion.copy(prevRot).slerp(tpQuat, b);
    }

    // ── Camera shake (external triggers add on top) ───────────────────────
    if (externalShakeRef && externalShakeRef.current > 0) {
      shakeIntensity.current = Math.max(shakeIntensity.current, externalShakeRef.current);
      shakeDecay.current = 0;
      externalShakeRef.current = 0;
    }
    if (shakeIntensity.current > 0.001) {
      shakeDecay.current += dt;
      const decay = Math.exp(-shakeDecay.current * 14);
      const si = shakeIntensity.current * decay;
      camera.rotation.x += (Math.random() - 0.5) * si * 0.8;
      camera.rotation.y += (Math.random() - 0.5) * si * 0.6;
      camera.rotation.z += (Math.random() - 0.5) * si * 0.4;
    }

    // ── Publish motion + game tick ────────────────────────────────────────
    // Only the editor overlay reads this back (nothing does yet, but it's
    // the editor's own scratch state), so there's no reason to pay a store
    // write - and notify every listener - on every frame of normal play.
    if (editorEnabled) {
      useMapEditorStore.setState({
        playerPosition: [player.position.x, player.position.y, player.position.z],
        playerYaw: yaw.current,
      });
    }
    if (motionStateRef) {
      motionStateRef.current = {
        yaw: yaw.current,
        pitch: pitch.current,
        blend: b,
        speed,
        isMoving,
        velocityX: velocity.current.x,
        velocityZ: velocity.current.z,
        floor: currentFloorRef.current,
        x: player.position.x,
        y: player.position.y,
        z: player.position.z,
      };
    }
    callbacksRef.current.onTick?.(dt, {
      yaw: yaw.current,
      pitch: pitch.current,
      blend: b,
      speed,
      isMoving,
      velocityX: velocity.current.x,
      velocityZ: velocity.current.z,
      floor: currentFloorRef.current,
      x: player.position.x,
      y: player.position.y,
      z: player.position.z,
    });
  });

  return (
    <group ref={playerRef}>
      <group ref={upperBodyRef} position={[0, 0, 0]}>
        {children}
      </group>
    </group>
  );
}

// ── Default player avatar ────────────────────────────────────────────────

/** Movement speed, as a fraction of the config's forward move speed, above which the avatar is considered sprinting. */
const SPRINT_SPEED_FRACTION = 0.9;

/** Blend value below which the avatar's head is fully collapsed (mostly first-person). */
const HEAD_HIDE_BLEND = 0.4;
/** Blend value above which the avatar's head is fully restored (mostly third-person). */
const HEAD_SHOW_BLEND = 0.65;
/** Non-zero so the mixer keeps evaluating the bone; visually indistinguishable from hidden. */
const HEAD_HIDDEN_SCALE = 0.001;

/**
 * Picks the avatar's locomotion clip from live motion state. There is no
 * dedicated sprint key today, so "sprinting" is approximated as movement near
 * the controller's forward speed cap. Pure strafing also reaches that cap (no
 * dedicated strafe clip exists either, see PROTOTYPE_CLIPS), so a strafe-only
 * player will read as sprinting; a known animation approximation, not a bug.
 */
export function selectLocomotionClip(
  motion: Pick<PlayerMotionState, 'isMoving' | 'speed'>,
  moveSpeed: number,
): PrototypeClip {
  if (!motion.isMoving) return 'Idle_Loop';
  const speedT = moveSpeed > 0 ? motion.speed / moveSpeed : 0;
  return speedT >= SPRINT_SPEED_FRACTION ? 'Sprint_Loop' : 'Walk_Loop';
}

/**
 * Scale for the avatar's head bone across the FP/TP camera blend: collapsed
 * to near-zero in first person (so the camera does not render from inside the
 * skull), restored once the blend is mostly third-person, smoothly
 * interpolated between. Collapsing a skinned bone's scale collapses the
 * vertices weighted to it; expect some neck-seam pinch during the transition,
 * a known limitation of the rig not being anatomically split into a separate
 * head mesh.
 */
export function headBoneScale(blend: number): number {
  const t = MathUtils.clamp((blend - HEAD_HIDE_BLEND) / (HEAD_SHOW_BLEND - HEAD_HIDE_BLEND), 0, 1);
  return MathUtils.lerp(HEAD_HIDDEN_SCALE, 1, t);
}

/**
 * A project character's own imported, pre-rigged GLB, standing in for the
 * stock prototype rig as the player's own body - resolved by GameRuntime.tsx
 * from the project roster entry a creator picked as the player character
 * (Game Setup's `playerCharacterId`). Pose-blended, not clip-driven: an
 * auto-rigged import has zero animation clips by construction, and a
 * cycling walk/run gait is out of scope here (see the player-avatar rig
 * wiring plan's trade-off note) - `idlePose`/`walkPose` are continuously
 * blended by the player's live speed fraction instead, a fixed mid-stride
 * lean rather than alternating limbs.
 */
export interface PlayerAvatarCustomBody {
  importedGlbUrl: string;
  /** Present only for a hand-mapped SF22 import (its keys are the stock
   * prototype rig's own bone names - BoneMappingPanel.tsx); absent for an
   * auto-rigged character, whose own bone names already match the
   * canonical rig by construction. */
  boneMapping?: Record<string, string>;
  idlePose: CharacterPose | null;
  walkPose: CharacterPose | null;
}

export interface PlayerAvatarProps {
  /** Live per-frame motion state. Pass the same ref given to the enclosing FirstPersonController's `motionStateRef` prop. */
  motionStateRef: MutableRefObject<PlayerMotionState | null>;
  /** Only `moveSpeed` and `eyeHeight` are read; pass the same config given to the controller so the avatar's pace and height line up. */
  config?: Partial<PlayerConfig>;
  /** Body/accent recolour of the shared rig. Ignored when `customBody` is set. */
  skin?: PrototypeSkin;
  /** When set, renders this character's own imported rig instead of the
   * stock prototype rig - see `PlayerAvatarCustomBody`'s own doc comment. */
  customBody?: PlayerAvatarCustomBody;
}

/**
 * The player's on-screen body: a project character's own imported rig when
 * `customBody` is set, the shared stock prototype rig (crossfaded between
 * idle/walk/sprint clips) otherwise. Pass as `FirstPersonController`'s
 * `children`.
 */
export function PlayerAvatar({ motionStateRef, config, skin, customBody }: PlayerAvatarProps) {
  if (customBody) {
    return <CustomBodyPlayerAvatar motionStateRef={motionStateRef} config={config} customBody={customBody} />;
  }
  return <StockPlayerAvatar motionStateRef={motionStateRef} config={config} skin={skin} />;
}

/** Resolves which of a custom rig's own bone names is its head, for the
 * same first-person head-hide `StockPlayerAvatar` applies (see
 * `headBoneScale`). `boneMapping`'s keys are the stock prototype rig's own
 * ~30 bone names (`BoneMappingPanel.tsx`), not the canonical 7-bone
 * vocabulary - so this looks up this rig's own head-bone name via the
 * prototype rig's own head-bone key. No mapping (an auto-rig export, or
 * nothing selected) resolves to `'head'`, correct because an auto-rig's own
 * bone names already are the canonical 7 by construction. */
export function resolvePlayerHeadBoneName(boneMapping?: Record<string, string>): string {
  return boneMapping?.[PROTOTYPE_SKELETON.headBone] ?? 'head';
}

/**
 * Scale `root` uniformly so its own bounding-box height matches
 * `targetMeters`, then settle it onto y=0. Deliberately a standalone
 * function rather than a reuse of `PrototypeInstance.fitHeight`
 * (`characters/animation/instance.ts`), which that file documents as kept
 * byte-identical with `sstk-sunder-web`'s own copy - an imported rig's own
 * arbitrary GLB is not that prototype rig.
 */
function fitObjectHeight(root: Object3D, targetMeters: number): void {
  root.scale.setScalar(1);
  root.updateMatrixWorld(true);
  const box = new Box3().setFromObject(root);
  const height = box.max.y - box.min.y;
  if (height > 0) {
    const factor = targetMeters / height;
    root.scale.setScalar(factor);
    root.updateMatrixWorld(true);
    const scaled = new Box3().setFromObject(root);
    root.position.y -= scaled.min.y;
  }
}

/** How quickly the idle/walk pose blend chases the player's current speed
 * fraction, in the same `MathUtils.damp` "rate" convention already used
 * throughout this file (e.g. `bodyFollowSpeed`). */
const CUSTOM_BODY_BLEND_DAMP_RATE = 8;

interface CustomBodyPlayerAvatarProps {
  motionStateRef: MutableRefObject<PlayerMotionState | null>;
  config?: Partial<PlayerConfig>;
  customBody: PlayerAvatarCustomBody;
}

function CustomBodyPlayerAvatar({ motionStateRef, config: configOverride, customBody }: CustomBodyPlayerAvatarProps) {
  const config = useMemo(() => ({ ...DEFAULT_PLAYER_CONFIG, ...configOverride }), [configOverride]);
  const rig = useImportedSkeletonRig(customBody.importedGlbUrl);
  const speedBlend = useRef(0);
  const headBoneName = useMemo(
    () => resolvePlayerHeadBoneName(customBody.boneMapping),
    [customBody.boneMapping],
  );

  useEffect(() => {
    if (!rig) return;
    // Same "fit to eye height / 0.93" convention StockPlayerAvatar uses -
    // see fitObjectHeight's own doc comment for why this is not a shared
    // helper with PrototypeInstance.fitHeight.
    fitObjectHeight(rig.scene, config.eyeHeight / 0.93);
    rig.scene.traverse((node) => {
      const m = node as Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
    // Runs once per load (on rig identity change), not on every eyeHeight
    // render - matches StockPlayerAvatar's own "fit once at load" behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rig]);

  useFrame((_, dt) => {
    if (!rig) return;
    const motion = motionStateRef.current;
    const speedT = motion && motion.isMoving && config.moveSpeed > 0
      ? MathUtils.clamp(motion.speed / config.moveSpeed, 0, 1)
      : 0;
    speedBlend.current = MathUtils.damp(speedBlend.current, speedT, CUSTOM_BODY_BLEND_DAMP_RATE, dt);
    const pose = blendPoses(customBody.idlePose, customBody.walkPose, speedBlend.current);
    for (const bone of rig.bones) applyPose(bone, pose, bone.name);
    if (motion) {
      const headBone = rig.bones.find((bone) => bone.name === headBoneName);
      if (headBone) headBone.scale.setScalar(headBoneScale(motion.blend));
    }
  });

  return rig ? <primitive object={rig.scene} /> : null;
}

interface StockPlayerAvatarProps {
  motionStateRef: MutableRefObject<PlayerMotionState | null>;
  config?: Partial<PlayerConfig>;
  skin?: PrototypeSkin;
}

/**
 * The stock player avatar: the shared prototype rig, crossfaded between
 * idle/walk/sprint from the controller's live motion state, with its head
 * collapsing to near-zero scale in first person so it does not clip the
 * camera.
 */
function StockPlayerAvatar({ motionStateRef, config: configOverride, skin }: StockPlayerAvatarProps) {
  const config = useMemo(() => ({ ...DEFAULT_PLAYER_CONFIG, ...configOverride }), [configOverride]);
  const [instance, setInstance] = useState<PrototypeInstance | null>(null);
  const headBoneRef = useRef<Object3D | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: PrototypeInstance | null = null;
    loadPrototypeGltf()
      .then((gltf) => {
        if (cancelled) return;
        created = createPrototypeInstance(gltf, skin);
        // An average adult's eye height is ~0.93x their stature, so fitting to
        // that puts the head bone right where the camera sits, which is what
        // makes the first-person head-collapse above actually work.
        created.fitHeight(config.eyeHeight / 0.93);
        created.root.traverse((node) => {
          if ((node as Mesh).isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
          }
        });
        headBoneRef.current = created.getBone(PROTOTYPE_SKELETON.headBone);
        created.play('Idle_Loop', { fade: 0 });
        setInstance(created);
      })
      .catch((err: unknown) => {
        console.error('Player avatar rig failed to load:', err);
      });
    return () => {
      cancelled = true;
      created?.dispose();
    };
    // The rig loads once per mount; live state below drives it via refs, not deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, dt) => {
    if (!instance) return;
    instance.update(dt);
    const motion = motionStateRef.current;
    if (!motion) return;
    instance.play(selectLocomotionClip(motion, config.moveSpeed), { fade: 0.2 });
    if (headBoneRef.current) headBoneRef.current.scale.setScalar(headBoneScale(motion.blend));
  });

  return instance ? <primitive object={instance.root} /> : null;
}
